import type { RefineThemedLayoutV2HeaderProps } from "@refinedev/antd";
import {
  useCreate,
  useGetIdentity,
  useGetLocale,
  useGo,
  useLogout,
  useSetLocale,
  useTranslate,
  useList,
  useLink,
  useUpdate,
} from "@refinedev/core";
import {
  Avatar,
  Layout as AntdLayout,
  Space,
  Switch,
  theme,
  Typography,
  Menu,
  Dropdown,
  Button,
  Flex,
  Popover,
  Badge,
  Tabs,
  List,
} from "antd";
import React, { useContext, useEffect, useState } from "react";
import { ColorModeContext } from "../../contexts/color-mode";
import { useTranslation } from "react-i18next";
import {
  DownOutlined,
  UserOutlined,
  BellOutlined,
  BellFilled,
  TeamOutlined,
  MessageOutlined,
  SettingOutlined,
  UserAddOutlined,
  CheckOutlined,
  LogoutOutlined,
  PlusOutlined,
  ContainerOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import { UserRole } from "../../models/role";
import { ActionButton } from "../../shared/components";
import { getInitials } from "../../shared/utils";
import { CreditBuyModal } from "../credit-buy-modal";
import { OpeningStatusBadge } from "../tenant/OpeningStatusBadge";

const { Text, Title } = Typography;
const { useToken } = theme;

type IUser = {
  id: number;
  name: string;
  avatar: string;
  firstName: string;
  lastName?: string;
  photo?: { path: string } | null;
  role?: {
    id: number;
  };
  account?: {
    id: number;
    billingMode: string;
    credit: number;
  };
};

type Notification = {
  id: number;
  title: string;
  message: string;
  category: "order" | "message" | "system";
  priority: "low" | "normal" | "high";
  isRead: boolean;
  createdAt: string;
  actionUrl?: string;
  metadata?: Record<string, any>;
};

export const Header: React.FC<RefineThemedLayoutV2HeaderProps> = ({
  sticky,
}) => {
  const { token } = useToken();
  const { data: user } = useGetIdentity<IUser>();
  const { mode, setMode } = useContext(ColorModeContext);
  const { mutate: logout } = useLogout();
  const _t = useTranslate();
  const { i18n } = useTranslation();
  const locale = useGetLocale();
  const changeLanguage = useSetLocale();
  const { mutate: create } = useCreate();
  const { mutate: markAsRead } = useUpdate();
  const currentLocale = locale();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isCreditModalOpen, setIsCreditModalOpen] = useState(false);
  const Link = useLink();
  const go = useGo();

  const {
    data: notificationsData,
    isLoading,
    refetch,
  } = useList<Notification>({
    resource: "notifications",
    pagination: {
      pageSize: 6,
    },
    filters: [
      {
        field: "channel",
        operator: "in",
        value: ["in_app", "both"],
      },
    ],
  });

  useEffect(() => {
    const intervalId = setInterval(() => {
      refetch();
    }, 120000); // 60000 ms = 1 minute

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, [refetch]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const menu = (
    <Menu selectedKeys={currentLocale ? [currentLocale] : []}>
      {[...(i18n.languages || [])].sort().map((lang: string) => (
        <Menu.Item
          key={lang}
          onClick={() => changeLanguage(lang)}
          icon={
            <span style={{ marginRight: 8 }}>
              <Avatar
                shape="square"
                size={16}
                src={`/images/flags/${lang}.svg`}
              />
            </span>
          }
        >
          {lang === "en" ? "English" : "French"}
        </Menu.Item>
      ))}
    </Menu>
  );

  const headerStyles: React.CSSProperties = {
    backgroundColor: token.colorBgLayout,
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    padding: "0px 24px",
    height: "92px",
    position: "sticky",
    top: 0,
    zIndex: isScrolled ? Infinity : 999,
    transition: "box-shadow 0.3s ease",
    boxShadow: isScrolled ? "0 2px 8px rgba(0, 0, 0, 0.1)" : "none",
  };

  if (sticky) {
    headerStyles.position = "sticky";
    headerStyles.top = 0;
    // Keep high z-index so scrolling content stays behind the header
    headerStyles.zIndex = 1000;
  }

  const items: MenuProps["items"] = [
    {
      key: "settings",
      label: (
        <Link to="/settings">
          <SettingOutlined style={{ marginRight: 8 }} />
          {_t("settings.preferences")}
        </Link>
      ),
    },
    {
      key: "logout",
      label: (
        <a onClick={() => logout()}>
          <LogoutOutlined style={{ marginRight: 8 }} />
          {_t("buttons.logout")}
        </a>
      ),
    },
  ];

  const getNotificationIcon = (category: Notification["category"]) => {
    const iconColor = token.colorSuccess;
    switch (category) {
      case "order":
        return <ContainerOutlined style={{ fontSize: 24, color: iconColor }} />;
      case "message":
        return <MessageOutlined style={{ fontSize: 24, color: iconColor }} />;
      case "system":
        return <BellOutlined style={{ fontSize: 24, color: iconColor }} />;
      default:
        return <BellOutlined style={{ fontSize: 24, color: iconColor }} />;
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60),
    );

    if (diffInMinutes < 60) {
      return _t("notifications.timeAgo.minutes", { count: diffInMinutes });
    } else if (diffInMinutes < 1440) {
      return _t("notifications.timeAgo.hours", {
        count: Math.floor(diffInMinutes / 60),
      });
    } else {
      return _t("notifications.timeAgo.days", {
        count: Math.floor(diffInMinutes / 1440),
      });
    }
  };

  const handleMarkAsRead = (notificationId: number) => {
    create(
      {
        resource: `notifications/${notificationId}/read`,
        values: {},
      },
      {
        onSuccess: () => {
          refetch();
        },
      },
    );
  };

  const handleMarkAllAsRead = () => {
    create(
      {
        resource: "notifications/read-all",
        values: {},
      },
      {
        onSuccess: () => {
          refetch();
        },
      },
    );
  };

  const renderNotificationItem = (item: Notification) => (
    <List.Item
      className={`p-4 border-b transition-colors duration-200 ${
        mode === "dark"
          ? "border-gray-700 hover:bg-gray-800"
          : "border-gray-200 hover:bg-gray-50"
      } ${item.actionUrl ? "cursor-pointer" : "cursor-default"}`}
      onClick={() => {
        if (item.actionUrl) {
          setIsNotificationOpen(false);
          go({
            to: item.actionUrl,
          });
        }
      }}
    >
      <List.Item.Meta
        avatar={
          <span
            className={`rounded-full p-2 inline-block ${
              mode === "dark" ? "bg-green-900/30" : "bg-green-50"
            }`}
          >
            {getNotificationIcon(item.category)}
          </span>
        }
        title={
          <span
            className={`font-semibold ${
              mode === "dark" ? "text-gray-100" : "text-gray-800"
            }`}
          >
            {item.title}
          </span>
        }
        description={
          <span className={mode === "dark" ? "text-gray-400" : "text-gray-600"}>
            {item.message}
          </span>
        }
      />
      <div className="flex items-center gap-4">
        <div
          className={`text-sm min-w-[70px] text-right ${
            mode === "dark" ? "text-gray-400" : "text-gray-600"
          }`}
        >
          {formatTimeAgo(item.createdAt)}
        </div>
        {!item.isRead && (
          <Button
            type="text"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleMarkAsRead(item.id);
            }}
            className="text-green-500 hover:text-green-600"
            icon={<CheckOutlined />}
          />
        )}
      </div>
    </List.Item>
  );

  const notificationContent = (
    <div className="w-[500px] max-w-[90vw]">
      <Tabs
        defaultActiveKey="all"
        items={[
          {
            key: "all",
            label: (
              <span className="flex items-center gap-2">
                {_t("notifications.all")}
                <Badge
                  count={
                    notificationsData?.data?.filter((n) => !n.isRead).length ||
                    0
                  }
                  className={`${
                    mode === "dark"
                      ? "bg-green-900/30 text-green-400"
                      : "bg-green-50 text-green-600"
                  }`}
                />
              </span>
            ),
            children: (
              <List
                itemLayout="horizontal"
                dataSource={notificationsData?.data?.slice(0, 6) || []}
                loading={isLoading}
                renderItem={renderNotificationItem}
              />
            ),
          },
          {
            key: "trainee",
            label: (
              <span className="flex items-center gap-2">
                {_t("notifications.tabs.order")}
                <Badge
                  count={
                    notificationsData?.data?.filter(
                      (n) => n.category === "order" && !n.isRead,
                    ).length || 0
                  }
                  className={`${
                    mode === "dark"
                      ? "bg-green-900/30 text-green-400"
                      : "bg-green-50 text-green-600"
                  }`}
                />
              </span>
            ),
            children: (
              <List
                itemLayout="horizontal"
                dataSource={
                  notificationsData?.data
                    ?.filter((n) => n.category === "order")
                    .slice(0, 6) || []
                }
                loading={isLoading}
                renderItem={renderNotificationItem}
              />
            ),
          },
          {
            key: "message",
            label: (
              <span className="flex items-center gap-2">
                {_t("notifications.tabs.message")}
                <Badge
                  count={
                    notificationsData?.data?.filter(
                      (n) => n.category === "message" && !n.isRead,
                    ).length || 0
                  }
                  className={`${
                    mode === "dark"
                      ? "bg-green-900/30 text-green-400"
                      : "bg-green-50 text-green-600"
                  }`}
                />
              </span>
            ),
            children: (
              <List
                itemLayout="horizontal"
                dataSource={
                  notificationsData?.data
                    ?.filter((n) => n.category === "message")
                    .slice(0, 6) || []
                }
                loading={isLoading}
                renderItem={renderNotificationItem}
              />
            ),
          },
          {
            key: "system",
            label: (
              <span className="flex items-center gap-2">
                {_t("notifications.tabs.system")}
                <Badge
                  count={
                    notificationsData?.data?.filter(
                      (n) => n.category === "system" && !n.isRead,
                    ).length || 0
                  }
                  className={`${
                    mode === "dark"
                      ? "bg-green-900/30 text-green-400"
                      : "bg-green-50 text-green-600"
                  }`}
                />
              </span>
            ),
            children: (
              <List
                itemLayout="horizontal"
                dataSource={
                  notificationsData?.data
                    ?.filter((n) => n.category === "system")
                    .slice(0, 6) || []
                }
                loading={isLoading}
                renderItem={renderNotificationItem}
              />
            ),
          },
        ]}
      />
      <div className="flex justify-between items-center p-3 border-t border-gray-200 dark:border-gray-700">
        <Button
          type="link"
          onClick={handleMarkAllAsRead}
          disabled={!notificationsData?.data?.some((n) => !n.isRead)}
          className={
            mode === "dark"
              ? "text-green-400 hover:text-green-300"
              : "text-green-600 hover:text-green-500"
          }
        >
          {_t("notifications.markAllAsRead", "Mark all as read")}
        </Button>
        <Button
          type="link"
          onClick={() => {
            setIsNotificationOpen(false);
            go({
              to: "/notifications",
            });
          }}
          className={
            mode === "dark"
              ? "text-blue-400 hover:text-blue-300"
              : "text-blue-600 hover:text-blue-500"
          }
        >
          {_t("notifications.viewAll")}
        </Button>
      </div>
    </div>
  );

  const accountId = user?.account?.id;

  return (
    <>
      <AntdLayout.Header style={headerStyles}>
        <Flex align="center" justify="space-between" className="w-full">
          <Flex align="center" justify="center">
            <div className="pl-2">
              <h1 className="pt-2 mb-0">
                {_t("titles.hello")} <b>{user?.firstName}</b>
              </h1>
              {user?.account?.billingMode === "prepaid" && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }} className="max-h-[20px]">
                  <WalletOutlined style={{ fontSize: 12, color: token.colorPrimary }} />
                  <span style={{ fontSize: 12, color: token.colorTextSecondary }}>
                    <span style={{ fontWeight: 600, color: user.account.credit === 0 ? token.colorError : token.colorPrimary }}>
                      {user.account.credit}
                    </span>{" "}
                    {_t("header.creditsAvailable")}
                  </span>
                  <span style={{ opacity: 0.4, fontSize: 12 }}>·</span>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => setIsCreditModalOpen(true)}
                    style={{ fontSize: 12, padding: 0, height: "auto", color: token.colorPrimary, fontWeight: 500 }}
                  >
                    {_t("header.buyCredits")}
                  </Button>
                </div>
              )}
              <OpeningStatusBadge className="mt-0.5" />
            </div>
          </Flex>
          <Flex align="center" justify="center">
            <Space>
              <Dropdown overlay={menu} className="hidden">
                <Button type="link">
                  <Space>
                    <Avatar shape="square" size={16} />
                    {currentLocale === "en" ? "English" : "Français"}
                    <DownOutlined />
                  </Space>
                </Button>
              </Dropdown>
              <Switch
                checkedChildren="🌛"
                unCheckedChildren="🔆"
                onChange={() => setMode()}
                defaultChecked={mode === "dark"}
              />

              {user?.role?.id == UserRole.user && (
                <ActionButton
                  variant="primary"
                  icon={<PlusOutlined />}
                  onClick={() =>
                    go({ to: `/accounts/${accountId}/orders/create` })
                  }
                  className="h-10"
                >
                  {_t("orders.titles.create")}
                </ActionButton>
              )}

              <Space size="middle" className="ml-4">
                <Popover
                  placement="bottomRight"
                  content={notificationContent}
                  trigger="click"
                  overlayStyle={{ padding: 0 }}
                  open={isNotificationOpen}
                  onOpenChange={setIsNotificationOpen}
                >
                  <Badge
                    count={
                      notificationsData?.data?.filter(
                        (notification) => !notification.isRead,
                      ).length || 0
                    }
                    showZero
                    offset={[-2, 2]}
                  >
                    <Button
                      type="default"
                      size="large"
                      className="rounded-xs"
                      icon={<BellFilled style={{ fontSize: 20 }} />}
                    />
                  </Badge>
                </Popover>

                <Dropdown menu={{ items }} placement="bottomRight" arrow>
                  <Button type="primary" size="large" className="rounded-xs px-3">
                    {getInitials(user?.firstName, user?.lastName)}
                  </Button>
                </Dropdown>
              </Space>
            </Space>
          </Flex>
        </Flex>
      </AntdLayout.Header>
      {user?.account?.billingMode === "prepaid" && (
        <CreditBuyModal
          isOpen={isCreditModalOpen}
          onCancel={() => setIsCreditModalOpen(false)}
          accountCredit={user?.account?.credit ?? 0}
        />
      )}
    </>
  );
};
